import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';

export type WithdrawalStatus = 'pending' | 'accepted' | 'paid' | 'rejected';

export interface WithdrawalStats {
  total: number;
  totalAmount: number;
  today: number;
  todayAmount: number;
  pending: number;
  pendingAmount: number;
  accepted: number;
  acceptedAmount: number;
  paid: number;
  paidAmount: number;
  rejected: number;
  rejectedAmount: number;
}

export interface WithdrawalListItem {
  id: string;
  userId: string | null;
  telegramId: string;
  userName: string;
  amount: number;
  currency: string;
  status: WithdrawalStatus;
  bankShortCode: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  note: string;
  rejectReason: string;
  createdAt: string;
}

export interface WithdrawalDetail extends WithdrawalListItem {
  userUsername: string;
  userPhone: string;
  userBalance: number;
  balanceAtRequest: number;
  newBalance: number | null;
  reviewedBy: string;
  reviewedAt: string | null;
  acceptedAt: string | null;
  paidAt: string | null;
  rejectedAt: string | null;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface WithdrawalsResponse {
  withdrawals: WithdrawalListItem[];
  pagination: Pagination;
}

export interface GetWithdrawalsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | WithdrawalStatus;
}

export interface UserSearchResult {
  id: string;
  telegramId: string;
  fullName: string;
  username: string;
  phoneNumber: string;
  balance: number;
}

export interface CreateWithdrawalInput {
  userId: string;
  amount: number;
  bankShortCode: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  note?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

class WithdrawalService {
  async getStats(): Promise<WithdrawalStats> {
    const res = await apiClient.get<ApiEnvelope<WithdrawalStats>>(
      API_ENDPOINTS.WITHDRAWALS.STATS
    );
    return res.data.data;
  }

  async getAll(params: GetWithdrawalsParams = {}): Promise<WithdrawalsResponse> {
    const res = await apiClient.get<ApiEnvelope<WithdrawalsResponse>>(
      API_ENDPOINTS.WITHDRAWALS.GET_ALL,
      { params }
    );
    return res.data.data;
  }

  async getOne(id: string): Promise<WithdrawalDetail> {
    const res = await apiClient.get<ApiEnvelope<WithdrawalDetail>>(
      API_ENDPOINTS.WITHDRAWALS.GET_ONE(id)
    );
    return res.data.data;
  }

  async create(input: CreateWithdrawalInput): Promise<{ id: string }> {
    const res = await apiClient.post<ApiEnvelope<{ id: string }>>(
      API_ENDPOINTS.WITHDRAWALS.CREATE,
      input
    );
    return res.data.data;
  }

  async accept(id: string): Promise<{ id: string; status: WithdrawalStatus }> {
    const res = await apiClient.patch<ApiEnvelope<{ id: string; status: WithdrawalStatus }>>(
      API_ENDPOINTS.WITHDRAWALS.ACCEPT(id)
    );
    return res.data.data;
  }

  async markPaid(id: string): Promise<{ id: string; status: WithdrawalStatus; newBalance: number }> {
    const res = await apiClient.patch<ApiEnvelope<any>>(
      API_ENDPOINTS.WITHDRAWALS.MARK_PAID(id)
    );
    return res.data.data;
  }

  async reject(id: string, reason: string): Promise<{ id: string; status: WithdrawalStatus }> {
    const res = await apiClient.patch<ApiEnvelope<{ id: string; status: WithdrawalStatus }>>(
      API_ENDPOINTS.WITHDRAWALS.REJECT(id),
      { reason }
    );
    return res.data.data;
  }

  async searchUsers(search: string): Promise<UserSearchResult[]> {
    const res = await apiClient.get<ApiEnvelope<UserSearchResult[]>>(
      API_ENDPOINTS.WITHDRAWALS.SEARCH_USERS,
      { params: { search } }
    );
    return res.data.data;
  }
}

const withdrawalService = new WithdrawalService();
export default withdrawalService;